import { CommonThreatsNavigationHelper } from './common.threats.navigation.helper';
import { Column, Filter } from '@nirvana/shared/dtos/types/shared/filters';
import { ActionTakenEnum, DetectingModules, DetectingTechnologies } from '@nirvana/shared/dtos/types/shared/threats';
import { RemediationActionsNavigationFiltersParams } from '../navigation.interfaces';

export class RemediationActionsNavigationHelper {
  public static getDisplayedColumns(): Array<Column> {
    return [Column.LAST_DETECTED, Column.DETECTING_MODULE, Column.DETECTING_TECHNOLOGY, Column.ACTION_TAKEN];
  }

  public static getFiltersForNavigationItem(params: RemediationActionsNavigationFiltersParams): Array<Filter> {
    const filters = [
      CommonThreatsNavigationHelper.getLastDetectedOnFilterFromTimeRange(params.timeRange),
      CommonThreatsNavigationHelper.getCategoryFilter(),
      RemediationActionsNavigationHelper.getActionsTakenFilter(params.itemKey),
      CommonThreatsNavigationHelper.getDeviceNameFilter(),
      RemediationActionsNavigationHelper.getDetectingModuleFilter(params.itemKey),
      RemediationActionsNavigationHelper.getDetectingTechnologyFilter(params.itemKey),
    ];

    return !params.isPartner ? filters : filters.concat(CommonThreatsNavigationHelper.getCompanyFilter(params.targets));
  }

  private static getActionsTakenFilter(itemKey: string) {
    let actionsTaken: Array<ActionTakenEnum>;

    switch (itemKey) {
      case 'title':
        actionsTaken = [
          ActionTakenEnum.BLOCKED,
          ActionTakenEnum.DELETED,
          ActionTakenEnum.QUARANTINED,
          ActionTakenEnum.REMEDIATED,
          ActionTakenEnum.MALICIOUS_PROCESS_KILLED,
          ActionTakenEnum.REPLACED_ATTACHMENT,
          ActionTakenEnum.DELETED_ATTACHMENT,
          ActionTakenEnum.REJECTED_EMAIL,
        ];
        break;
      case 'blocked':
        actionsTaken = [ActionTakenEnum.BLOCKED, ActionTakenEnum.REJECTED_EMAIL];
        break;
      case 'deleted':
        actionsTaken = [ActionTakenEnum.DELETED_ATTACHMENT, ActionTakenEnum.DELETED];
        break;
      case 'processesKilled':
        actionsTaken = [ActionTakenEnum.MALICIOUS_PROCESS_KILLED];
        break;
      case 'quarantined':
        actionsTaken = [ActionTakenEnum.QUARANTINED];
        break;
      case 'disinfected':
        actionsTaken = [ActionTakenEnum.REMEDIATED, ActionTakenEnum.REPLACED_ATTACHMENT];
        break;
      default:
        throw new Error(`Unknown category ${itemKey}.`);
    }

    return {
      actionTaken: {
        in: actionsTaken,
      },
    };
  }

  private static getDetectingModuleFilter(itemKey: string) {
    let detectingModules: Array<DetectingModules>;

    switch (itemKey) {
      case 'title':
      case 'blocked':
      case 'deleted':
        detectingModules = [
          DetectingModules.NETWORK_PROTECTION,
          DetectingModules.EXCHANGE_PROTECTION,
          DetectingModules.STORAGE_PROTECTION,
          DetectingModules.ANTIMALWARE,
        ];
        break;
      case 'quarantined':
      case 'disinfected':
        detectingModules = [
          DetectingModules.ANTIMALWARE,
          DetectingModules.STORAGE_PROTECTION,
          DetectingModules.EXCHANGE_PROTECTION,
        ];
        break;
      case 'processesKilled':
        detectingModules = [DetectingModules.ANTIMALWARE];
        break;
      default:
        throw new Error(`Unknown category ${itemKey}.`);
    }

    return {
      detectingModule: {
        in: detectingModules,
      },
    };
  }

  private static getDetectingTechnologyFilter(itemKey: string) {
    let detectingTechnologies: Array<DetectingTechnologies>;

    switch (itemKey) {
      case 'title':
      case 'blocked':
        detectingTechnologies = [
          DetectingTechnologies.ANTIMALWARE_ON_ACCESS,
          DetectingTechnologies.ANTIMALWARE_ON_DEMAND,
          DetectingTechnologies.ANTIMALWARE_ON_DEMAND_SCHEDULED_SCANS,
          DetectingTechnologies.ANTIMALWARE_CONTEXTUAL_SCAN,
          DetectingTechnologies.ANTIMALWARE_AVC,
          DetectingTechnologies.ANTIMALWARE_HYPER_DETECT,
          DetectingTechnologies.ANTIMALWARE_ON_EXECUTE_FILELESS_ATTACK_PROTECTION,
          DetectingTechnologies.ANTIMALWARE_ON_EXECUTE_ATC,
          DetectingTechnologies.ANTIMALWARE_ADVANCED_ANTI_EXPLOIT,
          DetectingTechnologies.NETWORK_PROTECTION_WEB_PROTECTION_WEB_TRAFFIC_SCAN,
          DetectingTechnologies.NETWORK_PROTECTION_WEB_PROTECTION_ANTIPHISING,
          DetectingTechnologies.NETWORK_PROTECTION_WEB_PROTECTION_HYPER_DETECT,
          DetectingTechnologies.NETWORK_PROTECTION_NETWORK_ATTACK_DEFENSE,
          DetectingTechnologies.STORAGE_PROTECTION_STORAGE_PROTECTION,
          DetectingTechnologies.EXCHANGE_PROTECTION_ANTIMALWARE,
        ];
        break;
      case 'deleted':
      case 'quarantined':
        detectingTechnologies = [
          DetectingTechnologies.ANTIMALWARE_ON_ACCESS,
          DetectingTechnologies.ANTIMALWARE_ON_DEMAND,
          DetectingTechnologies.ANTIMALWARE_ON_DEMAND_SCHEDULED_SCANS,
          DetectingTechnologies.ANTIMALWARE_CONTEXTUAL_SCAN,
          DetectingTechnologies.ANTIMALWARE_HYPER_DETECT,
          DetectingTechnologies.ANTIMALWARE_ON_EXECUTE_FILELESS_ATTACK_PROTECTION,
          DetectingTechnologies.STORAGE_PROTECTION_STORAGE_PROTECTION,
          DetectingTechnologies.EXCHANGE_PROTECTION_ANTIMALWARE,
        ];
        break;
      case 'processesKilled':
        detectingTechnologies = [DetectingTechnologies.ANTIMALWARE_ADVANCED_ANTI_EXPLOIT];
        break;
      case 'disinfected':
        detectingTechnologies = [
          DetectingTechnologies.ANTIMALWARE_ON_ACCESS,
          DetectingTechnologies.ANTIMALWARE_ON_DEMAND,
          DetectingTechnologies.ANTIMALWARE_ON_DEMAND_SCHEDULED_SCANS,
          DetectingTechnologies.ANTIMALWARE_CONTEXTUAL_SCAN,
          DetectingTechnologies.ANTIMALWARE_AVC,
          DetectingTechnologies.ANTIMALWARE_HYPER_DETECT,
          DetectingTechnologies.ANTIMALWARE_ON_EXECUTE_FILELESS_ATTACK_PROTECTION,
          DetectingTechnologies.ANTIMALWARE_ON_EXECUTE_ATC,
          DetectingTechnologies.STORAGE_PROTECTION_STORAGE_PROTECTION,
          DetectingTechnologies.EXCHANGE_PROTECTION_ANTIMALWARE,
        ];
        break;
      default:
        throw new Error(`Unknown category ${itemKey}.`);
    }

    return {
      detectingTechnology: {
        in: detectingTechnologies,
      },
    };
  }
}
